import { apiGet } from "../serverApi";
import { LocalStorer } from "./LocalStorer";
import { auctionHouseStorage } from "./storage";

export type LocalItemEntry = {
    itemId: number;
    itemName: string;
    qualityId: number;
    iconName: string;
    iconId: number;
    lastUpdate: number;
    data: ApiAuctionItemData[];
};

type ItemCacheStore = {
    lastClean: number;
    data: {
        [auctionHouseId: number]: {
            [itemId: number]: LocalItemEntry;
        };
    };
};

const itemStorer = new LocalStorer<ItemCacheStore>({
    storageKey: "itemCache",
    defaultData: { lastClean: 0, data: {} },
    memCache: true,
});

async function getAuctionHouseLastUpdate(auctionHouseId: number) {
    try {
        const ahData = await auctionHouseStorage.getData();
        for (const ah of ahData) {
            if (ah.id == auctionHouseId) return ah.lastUpdate;
        }
    } catch (error) {
        alert(`Failed to load auction house list: ${error}`);
    }
    return 0;
}

/**
 * Get cached data or load from API if no data or outdated.
 * @throws Error if any other error other than a 404 response happened.
 * @returns LocalItemEntry if item was cached or could be loaded from the API, undefined if item is not known (404).
 */
export async function getOrLoadItem(auctionHouseId: number, itemId: number): Promise<LocalItemEntry | undefined> {
    const data = itemStorer.getEntry("data");
    if (!data[auctionHouseId]) data[auctionHouseId] = {};
    const items = data[auctionHouseId];
    let itemData = items[itemId];
    const lastAhUpdate = await getAuctionHouseLastUpdate(auctionHouseId);

    if (!itemData || itemData.lastUpdate < lastAhUpdate) {
        const res = await apiGet<ApiAuctionItemDataResponse>(`/api/ahitem/${auctionHouseId}/${itemId}`);
        if (res.error) {
            if (res.error.statusCode != 404) {
                console.error(`Failed to get item data for ${itemId}: ${res.error.message}`);
                throw new Error(`${res.error.statusCode} | ${res.error.message}`);
            }
            return;
        }
        const apiItem = res.data;
        for (const v of apiItem.data) v.timestamp *= 1000;
        apiItem.data.sort((a, b) => a.timestamp - b.timestamp);
        const lastItemUpdate = apiItem.data[apiItem.data.length - 1]?.timestamp ?? -1;
        itemData = {
            itemId: apiItem.itemId,
            itemName: apiItem.itemName,
            qualityId: apiItem.qualityId,
            iconName: apiItem.iconName,
            iconId: apiItem.iconId,
            lastUpdate: Math.max(lastAhUpdate, lastItemUpdate),
            data: apiItem.data,
        };
        items[itemId] = itemData;
        itemStorer.setEntry("data", data);
    }

    return itemData;
}

(() => {
    const day = 86400_000;
    const lastClean = itemStorer.getEntry("lastClean");
    const now = Date.now();
    let delCount = 0;
    if (lastClean + day < now) {
        const data = itemStorer.getEntry("data");
        for (const ahId in data) {
            const items = data[ahId]!;
            for (const itemId in items) {
                if (items[itemId]!.lastUpdate + day < now) {
                    delete items[itemId];
                    delCount++;
                }
            }
        }
    }
    if (delCount) console.log(`Removed ${delCount} old items from item cache.`);
    itemStorer.setEntry("lastClean", now);
})();
